/**
 * Types for the Contract Explorer list.
 *
 * `DatabagContractApi` mirrors one row of `GET /data/{databag_id}/contracts/`
 * verbatim (snake_case, as cat-material serves it); `ClContractRow` is the
 * camelCase shape the UI works with. Mapping happens once, in the query hook.
 *
 * The list arrives in DRF's LimitOffset envelope (`PageResponse` from
 * `~/services/ApiTypes.ts`: `count` / `next` / `previous` / `results`); the
 * whole-databag figures come from `GET …/contracts/summary/`
 * (`ContractSummaryResponse`).
 */

/**
 * The contract's single lifecycle state, computed by the backend from the
 * signing stage and the dates against a reference date:
 * draft → signed (not yet effective) → active → expiring → expired.
 */
export type ClContractLifecycle = 'draft' | 'signed' | 'active' | 'expiring' | 'expired';

export const LIFECYCLES: ClContractLifecycle[] = ['draft', 'signed', 'active', 'expiring', 'expired'];

export type DatabagContractApi = {
    id: number;
    file_name: string;
    source_relative_path: string;
    contract_title: string;
    contract_type: string;
    /** Raw value as extracted by the parser (kept for reference; prefer `stage`). */
    contract_status: string;
    lifecycle: ClContractLifecycle;
    /** contract_type mapped onto the backend's canonical list. */
    contract_type_clean: string;
    /** False when the parsed type is not in that list (then it is shown as parsed). */
    contract_type_is_known: boolean;
    supplier_name: string;
    /** ISO yyyy-mm-dd, or null when the parser found none. */
    effective_date: string | null;
    expiration_date: string | null;
    contracted_amount: number | null;
    version_no: number;
    has_review: boolean;
    has_file: boolean;
    parsed_at: string | null;
};

export type ContractTypeCount = {contract_type: string; is_known: boolean; count: number};

export type ContractLifecycleCounts = Record<ClContractLifecycle, number>;

/**
 * `GET /data/{databag_id}/contracts/summary/` — the whole-databag figures,
 * independent of paging, search and filters. Fetched once per databag.
 */
export type ContractSummaryResponse = {
    /** Reference date the statuses were calculated against (ISO yyyy-mm-dd). */
    as_of: string;
    /** Whole-databag counts, independent of paging/filters. */
    lifecycle_counts: ContractLifecycleCounts;
    /** The contract types present in this databag, with counts. */
    type_counts: ContractTypeCount[];
    /** The backend's canonical type vocabulary, for the review form. */
    known_types: string[];
    /** Customer base currency all amounts are in. */
    currency: string;
};

/** What the query hook hands the page: one window of rows plus the filtered total. */
export type ContractListData = {
    rows: ClContractRow[];
    total: number;
};

export type ClContractRow = {
    id: number;
    title: string;
    fileName: string;
    supplier: string;
    /** Cleaned type, labelled for display. */
    type: string;
    lifecycle: ClContractLifecycle;
    effectiveDate: string | null;
    expirationDate: string | null;
    contractedAmount: number;
    hasFile: boolean;
    hasReview: boolean;
    versionNo: number;
};

export function toContractRow(c: DatabagContractApi): ClContractRow {
    return {
        id: c.id,
        title: c.contract_title || c.file_name,
        fileName: c.file_name,
        supplier: c.supplier_name,
        type: c.contract_type_is_known ? typeLabel(c.contract_type_clean) : (c.contract_type_clean || c.contract_type),
        lifecycle: c.lifecycle,
        effectiveDate: c.effective_date,
        expirationDate: c.expiration_date,
        contractedAmount: c.contracted_amount ?? 0,
        hasFile: c.has_file,
        hasReview: c.has_review,
        versionNo: c.version_no,
    };
}

export const LIFECYCLE_LABEL: Record<ClContractLifecycle, string> = {
    draft: 'Draft',
    signed: 'Signed',
    active: 'Active',
    expiring: 'Expiring',
    expired: 'Expired',
};

/** `lease_agreement` -> `Lease agreement`; unknown values arrive already normalized. */
export function typeLabel(value: string): string {
    if (!value) return '—';
    const words = value.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

export const LIFECYCLE_COLOR = {
    draft: 'default',
    signed: 'info',
    active: 'success',
    expiring: 'warning',
    expired: 'error',
} as const;

/** Days until the contract expires, relative to the backend's reference date. */
export function daysUntilExpiry(expiration: string | null, asOf: string): number | null {
    if (!expiration || !asOf) return null;
    const days = Math.round(
        (Date.parse(expiration.slice(0, 10)) - Date.parse(asOf.slice(0, 10))) / 86_400_000,
    );
    return Number.isFinite(days) ? days : null;
}

export const formatDate = (isoDate: string | null): string => {
    if (!isoDate) return '—';
    const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
    if (!y || !m || !d) return '—';
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
};

export const formatAmount = (value: number, currency?: string): string => {
    if (!(value > 0)) return '—';
    const text = new Intl.NumberFormat('en-US', {notation: 'compact', maximumFractionDigits: 1}).format(value);
    return currency ? `${text} ${currency}` : text;
};
